import React from 'react';
import { Link } from 'react-router-dom';
type LogoSize = 'sm' | 'md' | 'lg';
type LogoTone = 'onDark' | 'onLight';
interface LogoProps {
  size?: LogoSize;
  /** onDark: reversed version (mountain in white) for charcoal backgrounds. onLight: original colours. */
  tone?: LogoTone;
  asLink?: boolean;
  className?: string;
}
const sizes: Record<LogoSize, string> = {
  sm: 'h-[52px]',
  md: 'h-[76px]',
  lg: 'h-[100px]'
};
const RED = '#EE3725';
const CHARCOAL = '#231F20';
const WHITE = '#FFFFFF';

/**
 * Vector redraw of the original Liftoff Investments logo (lift off logo.pdf),
 * keeping its proportions: stepped mountain mark, red "LIFT" + white "OFF" on a
 * charcoal slanted plate, and the red "INVESTMENTS (PTY) LTD" line.
 */
export function Logo({
  size = 'md',
  tone = 'onDark',
  asLink = true,
  className = ''
}: LogoProps) {
  const content = <LogoSvg tone={tone} className={`${sizes[size]} w-auto ${className}`} data-id="element-464" data-mp="2395n2l30kazp" />;
  if (!asLink) return content;
  return <Link to="/" aria-label="Liftoff Investments home" className="inline-flex shrink-0" data-id="element-465" data-mp="1e7pr97fezkoe">
      {content}
    </Link>;
}
function LogoSvg({
  tone,
  className
}: {
  tone: LogoTone;
  className: string;
}) {
  const stripe = tone === 'onDark' ? WHITE : CHARCOAL;
  const skew = 'translate(0 556) skewX(-13) translate(0 -556)';
  return <svg viewBox="340 208 630 404" className={className} role="img" aria-label="Liftoff Investments (Pty) Ltd" focusable="false" data-id="element-466" data-mp="199o68gyfhbaa">
      {/* Mountain mark: red slivers behind, stripes on top */}
      <g data-id="element-467" data-mp="21wimf1o7pohy">
        <polygon points="612,260 690,463 622,463" fill={RED} data-id="element-468" data-mp="21zejyow5eb5v" />
        <polygon points="578,328 622,463 579,463" fill={RED} data-id="element-469" data-mp="1o544ky61jtch" />
        <polygon points="542,395 580,463 543,463" fill={RED} data-id="element-470" data-mp="1s78xfuoo0ly2" />
        <polygon points="612,260 634,215 757,463 712,463" fill={stripe} data-id="element-471" data-mp="1s5nxtqnhuctc" />
        <polygon points="578,328 599,285 690,463 645,463" fill={stripe} data-id="element-472" data-mp="2fw9xatjfmorq" />
        <polygon points="542,395 566,352 622,463 578,463" fill={stripe} data-id="element-473" data-mp="2kobe8srjp3o3" />
        <polygon points="512,463 533,423 557,463" fill={stripe} data-id="element-474" data-mp="2ujs3xxgk1tgs" />
      </g>

      {/* Wordmark */}
      <g transform={skew} data-id="element-475" data-mp="2qi6wirmsi7dz">
        <rect x="352" y="470" width="596" height="102" rx="12" fill={CHARCOAL} data-id="element-476" data-mp="2qhx4kyi6pq5n" />
        {/* LIFT */}
        <path d="M362 486h22v54h41v16h-63z" fill={RED} data-id="element-477" data-mp="2liaqynyg27wb" />
        <rect x="440" y="486" width="22" height="70" fill={RED} data-id="element-478" data-mp="2lkwyinlc4nej" />
        <path d="M484 486h84v16h-62v14h48v15h-48v25h-22z" fill={RED} data-id="element-479" data-mp="2gjhd2uf8wevm" />
        <path d="M578 486h80v16h-31v54h-22v-54h-27z" fill={RED} data-id="element-480" data-mp="2bl62ghh4m0cc" />
        {/* OFF */}
        <rect x="664" y="486" width="82" height="70" rx="16" fill={WHITE} data-id="element-481" data-mp="26ufv4yp53nf0" />
        <rect x="688" y="508" width="34" height="26" rx="5" fill={CHARCOAL} data-id="element-482" data-mp="26scxptlnqs09" />
        <rect x="697" y="515" width="16" height="10" rx="2" fill={WHITE} data-id="element-483" data-mp="22sucsxtu6gxf" />
        <path d="M768 486h84v16h-62v14h50v15h-50v25h-22z" fill={WHITE} data-id="element-484" data-mp="1xusvahc26po2" />
        <path d="M856 486h86v16h-64v14h50v15h-50v25h-22z" fill={WHITE} data-id="element-485" data-mp="1suy2u9cmxs0w" />
      </g>

      <text x="662" y="605" fill={RED} fontFamily="Archivo, Inter, sans-serif" fontStyle="italic" fontWeight={700} fontSize="26" textLength="288" lengthAdjust="spacingAndGlyphs" data-id="element-486" data-mp="1nvbv2ffkt3zh">
        INVESTMENTS (PTY) LTD
      </text>
    </svg>;
}